import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from '@mybridge/ui/button';
import { Input } from '@mybridge/ui/input';
import {
  updateUserDetails,
  getUserProfileInfo,
} from 'packages/mybridgev4/store/actions/user-profile.actions';
import {
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  SimpleGrid,
  Text,
  Spinner,
} from '@mybridge/ui';
import { AddCircle, BackArrow, BackArrowRTL, EditIcon, PersonIcon } from '@mybridge/icons';
import styles from './settings.module.scss';
import { isMobile } from 'react-device-detect';
import { IconButton } from '@mybridge/ui/icon-button';
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';
import { HStack } from '@chakra-ui/react';
import { useDirection } from '@mybridge/ui/custom-hooks/useDirection';

const Name = ({ setIsEditing,setSelectedAccountComponent,setEditMode,editMode }) => {
  const dispatch = useDispatch();
  const { t } = useTranslator();
  const  direction = useDirection();
  const { userProfileInfo, isProfileUpdated, updateStatus } = useSelector(
    (state) => state.userProfile
  );

  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const [hasMounted, setHasMounted] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setHasMounted(true);
    }, 5000); // 1 minute = 60000 ms
  
    // Cleanup function to clear the timer
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (userProfileInfo?.id) {
      setValues(userProfileInfo);
    }

    return () => setValues({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfileInfo]);

  useEffect(() => {
    if (isProfileUpdated) {
      setEditMode(false);
      dispatch(getUserProfileInfo());
    }
  }, [isProfileUpdated, dispatch]);

  const fieldsOnChange = (e) => {
    const { name, value } = e.target;
    setValues((prevValues) => ({
      ...prevValues,
      [name]: value ? value : null,
    }));
  };

  const onClickSave = () => {
    const errorObject = {};
    if (!values.first_name || !values.last_name) {
      errorObject.name = 'Please enter first name and last name';
    }

    if (Object.keys(errorObject).length > 0) {
      setErrors(errorObject);
      return;
    }

    const payload = {};
    if (values.first_name !== userProfileInfo?.first_name) {
      payload.first_name = values.first_name || '';
    }
    if (values.last_name !== userProfileInfo?.last_name) {
      payload.last_name = values.last_name || '';
    }
    if (values.former_name !== userProfileInfo?.former_name) {
      payload.former_name =
        values.former_name === '' ? null : values.former_name;
    }

    dispatch(updateUserDetails(payload));

    // Update the local state immediately after saving
    setValues((prevValues) => ({
      ...prevValues,
      ...payload,
    }));
    setEditMode(false); // Close the edit mode after saving
    setIsEditing('')
  };

  const onClickCancel = () => {
    setEditMode(false);
    setErrors({});
    setValues(userProfileInfo);
    setIsEditing('');
    setSelectedAccountComponent('account');
  };

  return (
    <Box mb={['0px', '15px']}>
      <Flex justifyContent="space-between" p={['10px', '0']}>
        {/* <Box w={['100%', '350px']}> */}
        <Box flex={1} maxW="350px" mr={4} minW="0">
          {isMobile && !editMode && (
            <>
              {/* <Box mt={3} display="flex" alignItems="center"> */}
              <Box mt={3} display="flex" alignItems="center" minW="0">
                <PersonIcon
                  style={{ marginRight: '10px' }}
                  color="#868585"
                  height="24"
                  width="24"
                />
                <Text
                  color="#868585"
                  width="90%" // Set the width to 90%
                  whiteSpace="nowrap" // Prevent text from wrapping to the next line
                  overflow="hidden" // Hide overflow text
                  textOverflow="ellipsis"
                >
                  {userProfileInfo.first_name}{' '}
                  {userProfileInfo.former_name &&
                  userProfileInfo.former_name !== 'null'
                    ? `(${userProfileInfo?.former_name})`
                    : ''}{' '}
                  {userProfileInfo.last_name}
                </Text>
              </Box>
              <Text color="#868585" fontSize={['12px']} ml="35px">
                {t('myDisk.name') || "Name"}
              </Text>
            </>
          )}
          {!isMobile && (
            <>
              {editMode ? (
                <>
                  <HStack>
                    <Button
                      variant="transparent"
                      leftIcon={direction === 'rtl' ? <BackArrowRTL color="#868585" /> : <BackArrow color="#868585" />}
                      color="#868585"
                      onClick={onClickCancel}
                      p={0}
                    />
                    <Text color="brandGray.700" fontSize={['20px', '16px']}>
                      {t('myDisk.name') || "Name"}
                    </Text>
                  </HStack>
                  <Text fontSize="sm" color="brandGray.700" mt="10px">
                    {t('settingAccount.nameDescription') || "This is how your name appears to other members"}
                  </Text>
                  <Box className={styles.dataDisplayBoxes} mt={3}>
                    {userProfileInfo.first_name}{' '}
                    {userProfileInfo.former_name &&
                    userProfileInfo.former_name !== 'null'
                      ? `(${userProfileInfo?.former_name})`
                      : ''}{' '}
                    {userProfileInfo.last_name}
                  </Box>
                </>
              ) : (
                <>
                  {/* <Text color="brandGray.700" fontSize={['20px', '16px']}> */}
                  <Text color="brandGray.700" fontSize={['20px', '14px', '14px', '16px']}>
                    {t('myDisk.name') || "Name"}
                  </Text>
                  <Text fontSize="sm" color="brandGray.700" mt="10px">
                    {t('settingAccount.nameDescription') || "This is how your name appears to other members"}
                  </Text>
                  <Box className={styles.dataDisplayBoxes} mt={3}>
                    {userProfileInfo.first_name}{' '}
                    {userProfileInfo.former_name &&
                    userProfileInfo.former_name !== 'null'
                      ? `(${userProfileInfo?.former_name})`
                      : ''}{' '}
                    {userProfileInfo.last_name}
                  </Box>
                </>
              )}
            </>
          )}
          {editMode && !isMobile && (
            <Box mt={3}>
              <hr />
            </Box>
          )}
        </Box>
        {isMobile && !editMode ? (
          <IconButton
            bg="none"
            onClick={() => {
              setEditMode(true);
              setIsEditing('name');
              setSelectedAccountComponent('name');
            }}
            icon={<EditIcon />}
          />
        ) : (
          !editMode && (
            <Button
              borderRadius={"30px"}
              variant="ghost"
              color="black"
              onClick={() => {
                setEditMode(true);
                setIsEditing('name');
                setSelectedAccountComponent('name');
              }}
              leftIcon={<AddCircle width={20} height={20} />}
              fontSize={['14px', '13px', '13px', '14px']}
            >
              {t('settingAccount.editName') || "Edit name"}
            </Button>
          )
        )}
      </Flex>
      {editMode && (
        <Box p={['10px', '0']}>
          {isMobile && (
            <Text color="brandGray.700" fontSize={['20px', '16px']}>
              {t('myDisk.name') || "Name"}
            </Text>
          )}
          <Text fontSize={['md', 'sm']} color="brandGray.700" mt="10px">
            {t('settingAccount.chooseNameAppearance') || "Choose how your name appears to other members"}
          </Text>
          {isMobile ? (
            <Flex flexDirection="column" gap={3} mt="5%">
              <FormControl>
                <Input
                  type="text"
                  name="first_name"
                  value={values.first_name || ''}
                  onChange={fieldsOnChange}
                  placeholder={t('settingAccount.enterFirstName') || "Enter First Name"}
                  className={styles.dataDisplayBoxes}
                />
                {errors.name && (
                  <FormErrorMessage>{errors.name}</FormErrorMessage>
                )}
              </FormControl>
              <FormControl>
                <Input
                  type="text"
                  name="last_name"
                  value={values.last_name || ''}
                  onChange={fieldsOnChange}
                  placeholder={t('settingAccount.enterLastName') || "Enter Last Name"}
                  className={styles.dataDisplayBoxes}
                />
              </FormControl>
              <FormControl>
                <Input
                  type="text"
                  name="former_name"
                  value={
                    values.former_name !== 'null'
                      ? values.former_name || ''
                      : ''
                  }
                  onChange={fieldsOnChange}
                  placeholder={t('settingAccount.formerName') || "Former Name"}
                  className={styles.dataDisplayBoxes}
                />
              </FormControl>
            </Flex>
          ) : (
            <SimpleGrid
              spacing={3}
              columns={2}
              my="10px"
              w={['100%', '100%', '75%']}
              className={styles.simpleGrid} // Add class for mobile styles
            >
              <FormControl
                isInvalid={errors.name}
                className={styles.formControl}
              >
                <Input
                  type="text"
                  name="first_name"
                  value={values.first_name || ''}
                  onChange={fieldsOnChange}
                  placeholder={t('settingAccount.enterFirstName') || "Enter First Name"}
                  variant="greyRounded"
                />
                {errors.name && (
                  <FormErrorMessage>{errors.name}</FormErrorMessage>
                )}
              </FormControl>
              <FormControl className={styles.formControl}>
                <Input
                  type="text"
                  name="last_name"
                  value={values.last_name || ''}
                  onChange={fieldsOnChange}
                  placeholder={t('settingAccount.enterLastName') || "Enter Last Name"}
                  variant="greyRounded"
                />
              </FormControl>
              <FormControl className={styles.formControl}>
                <FormLabel>{t('settingAccount.formerName') || "Former Name"}</FormLabel>
                <Input
                  type="text"
                  name="former_name"
                  value={
                    values.former_name !== 'null'
                      ? values.former_name || ''
                      : ''
                  }
                  onChange={(e) => {
                    if (hasMounted) fieldsOnChange(e);
                  }} 
                  placeholder={t('settingAccount.formerName') || "Former Name"}
                  variant="greyRounded"
                />
              </FormControl>
            </SimpleGrid>
          )}

          <Flex
            justifyContent={isMobile ? 'flex-end' : 'flex-start'}
            columnGap="2"
            mt={['5%', '2%']}
          >
            <Button
              onClick={onClickCancel}
              borderRadius={['30px', '30px']}
              bg="#E4E6EB"
            >
              {t('buttons.cancel') || "Cancel"}
            </Button>
            <Button
              disabled={updateStatus === 'pending'}
              onClick={onClickSave}
              color="#fff"
              bg={['#3D5A80', '#3D5A80']}
              borderRadius={['30px', '30px']}
            >
              {updateStatus === 'pending' ? <Spinner /> : t('buttons.save') || 'Save'}
            </Button>
          </Flex>
        </Box>
      )}
    </Box>
  );
};

export default Name;
